import React, { useMemo, useCallback, useState } from 'react';
import { Input, Button, Space, Card, Grid, Flex } from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import SharedFilterBar from '../../Shared/Filters/SharedFilterBar';
import { credentialFilterFields } from '../../Shared/Filters/filterConfig';
import AppTable from '../../Shared/AppTable/AppTable';
import { TABLE_CLASS, getRowClassName, getColumns } from './constants';
import SkeletonTable from './SkeletonTable';

const { useBreakpoint } = Grid;

const CredentialTable = ({
    credentials,
    loading,
    isInitialLoading,
    pagination,
    searchString,
    filters,
    sorter,
    onTableChange,
    onSearch,
    onFilterChange,
    onAction,
    onAdd,
    highlightedRowIds,
    softRefreshKey,
    deleteLoading,
}) => {
    const { t } = useTranslation();
    const screens = useBreakpoint() || {};
    const [isInputFocused, setIsInputFocused] = useState(false);

    const columns = useMemo(() => getColumns(t, screens, onAction, deleteLoading), [t, screens, onAction, deleteLoading]);

    const handleSearch = useCallback((e) => {
        onSearch(e.target.value);
    }, [onSearch]);

    const handleClearSearch = useCallback(() => {
        onSearch('');
    }, [onSearch]);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card
                title={t('credentials')}
                extra={
                    <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={onAdd}
                    >
                        {!screens.xs && t('add_credential')}
                    </Button>
                }
                styles={{
                    body: {
                        padding: 16
                    }
                }}
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                    align="center"
                    justify="space-between"
                    style={{ width: '100%' }}
                >
                    <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                        <Input
                            placeholder={t('search')}
                            value={searchString}
                            onChange={handleSearch}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            allowClear
                            onClear={handleClearSearch}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                                transition: 'width 0.4s ease-in-out'
                            }}
                            prefix={<SearchOutlined />}
                        />
                    </Flex>

                    <SharedFilterBar
                        fields={credentialFilterFields}
                        value={filters}
                        onChange={onFilterChange}
                        sortFields={[
                            { value: 'channel_type', labelKey: 'filters_field_channel_type' },
                            { value: 'name', labelKey: 'name' },
                            { value: 'first_name', labelKey: 'first_name' },
                            { value: 'last_name', labelKey: 'last_name' },
                            { value: 'email', labelKey: 'email' },
                            { value: 'credential_user_id', labelKey: 'credential_user_id' },
                            { value: 'token_expires_at', labelKey: 'token_expires_at' },
                            { value: 'is_permanent_token', labelKey: 'filters_field_permanent_token' },
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                        ]}
                        sortValue={{ sort_by: sorter.field, sort_direction: sorter.order }}
                        onSortChange={(sort) => {
                            onTableChange({ ...pagination, current: 1 }, filters, { field: sort.sort_by, order: sort.sort_direction === 'asc' ? 'ascend' : (sort.sort_direction === 'desc' ? 'descend' : undefined) });
                        }}
                    />
                </Flex>
            </Card>

            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                {isInitialLoading ? (
                    <SkeletonTable columns={columns} rowCount={10} active={true} />
                ) : (
                    <AppTable
                        key={softRefreshKey}
                        className={TABLE_CLASS}
                        columns={columns}
                        dataSource={credentials}
                        rowKey="id"
                        scroll={{ x: 800 }}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: !screens.xs,
                        }}
                        onChange={onTableChange}
                        rowClassName={(record) => getRowClassName(record, highlightedRowIds)}
                    />
                )}
            </Card>
        </Space>
    );
};

export default CredentialTable;
